import type {
  AppEvent, PurchaseCorrectedPayload, PurchaseDeletedPayload, PurchaseLoggedPayload,
} from '../core/types.js';

function isLoggedPayload(p: unknown): p is PurchaseLoggedPayload {
  if (typeof p !== 'object' || p === null) return false;
  const o = p as Record<string, unknown>;
  return typeof o['purchaseId'] === 'string'
    && typeof o['label'] === 'string'
    && typeof o['currency'] === 'string'
    && typeof o['amount'] === 'number' && isFinite(o['amount'] as number);
}

function isCorrectedPayload(p: unknown): p is PurchaseCorrectedPayload {
  if (typeof p !== 'object' || p === null) return false;
  const o = p as Record<string, unknown>;
  return typeof o['purchaseId'] === 'string';
}

function isDeletedPayload(p: unknown): p is PurchaseDeletedPayload {
  if (typeof p !== 'object' || p === null) return false;
  const o = p as Record<string, unknown>;
  return typeof o['purchaseId'] === 'string';
}

export interface ResolvedPurchase {
  id: string;
  templateId?: string | undefined;
  methodId?: string | undefined;
  habitId?: string | undefined;
  label: string;
  amount: number;
  currency: string;
  quantityPackages: number;
  packageQuantity?: number | undefined;
  occurredAt: string;
  createdAt: string;
}

function compareEvents(a: AppEvent, b: AppEvent): number {
  if (a.occurredAt !== b.occurredAt) return a.occurredAt < b.occurredAt ? -1 : 1;
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Deterministically resolves purchase events into the current set of purchases.
 * Honors logged → corrected → deleted ordering by (occurredAt, createdAt, id).
 */
export function resolvePurchases(rawEvents: AppEvent[]): ResolvedPurchase[] {
  const purchases = new Map<string, ResolvedPurchase>();
  const deleted = new Set<string>();
  const sorted = rawEvents
    .filter((e) =>
      e.status !== 'undone' && (
        e.type === 'purchase_logged'
        || e.type === 'purchase_corrected'
        || e.type === 'purchase_deleted'
      ),
    )
    .slice()
    .sort(compareEvents);

  for (const ev of sorted) {
    if (ev.type === 'purchase_logged') {
      if (!isLoggedPayload(ev.payload)) continue;
      const p = ev.payload;
      if (deleted.has(p.purchaseId)) continue;
      const qty = p.quantityPackages > 0 ? p.quantityPackages : 1;
      const rec: ResolvedPurchase = {
        id: p.purchaseId,
        label: p.label,
        amount: p.amount * qty,
        currency: p.currency,
        quantityPackages: qty,
        occurredAt: ev.occurredAt,
        createdAt: ev.createdAt,
      };
      if (p.templateId !== undefined) rec.templateId = p.templateId;
      if (p.methodId !== undefined) rec.methodId = p.methodId;
      if (ev.habitId !== undefined) rec.habitId = ev.habitId;
      if (p.packageQuantity !== undefined) rec.packageQuantity = p.packageQuantity;
      purchases.set(p.purchaseId, rec);
      continue;
    }
    if (ev.type === 'purchase_corrected') {
      if (!isCorrectedPayload(ev.payload)) continue;
      const c = ev.payload;
      if (deleted.has(c.purchaseId)) continue;
      const existing = purchases.get(c.purchaseId);
      if (!existing) continue;
      if (typeof c.correctedAmount === 'number') {
        existing.amount = c.correctedAmount * existing.quantityPackages;
      }
      if (typeof c.correctedQuantityPackages === 'number' && c.correctedQuantityPackages > 0) {
        const perPackage = existing.amount / existing.quantityPackages;
        existing.quantityPackages = c.correctedQuantityPackages;
        existing.amount = perPackage * c.correctedQuantityPackages;
      }
      if (c.correctedCurrency) existing.currency = c.correctedCurrency;
      if (c.correctedLabel) existing.label = c.correctedLabel;
      continue;
    }
    if (ev.type === 'purchase_deleted') {
      if (!isDeletedPayload(ev.payload)) continue;
      deleted.add(ev.payload.purchaseId);
      purchases.delete(ev.payload.purchaseId);
    }
  }

  return [...purchases.values()];
}

export function purchasesInRange(
  purchases: ResolvedPurchase[],
  fromIso: string,
  toIso: string,
): ResolvedPurchase[] {
  return purchases.filter((p) => p.occurredAt >= fromIso && p.occurredAt <= toIso);
}

export function purchasesForHabit(
  purchases: ResolvedPurchase[],
  habitId: string,
): ResolvedPurchase[] {
  return purchases.filter((p) => p.habitId === habitId);
}
